import { useState, ChangeEvent, FormEvent, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  useTheme,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faComments } from "@fortawesome/free-solid-svg-icons";
import emailjs from "@emailjs/browser";

interface FormData {
  name: string;
  email: string;
  phone: string;
  serviceType: string;
  message: string;
}

const ContactForm = () => {
  const theme = useTheme();
  const form = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    serviceType: "",
    message: "",
  });

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("Form submitted:", formData);

    // 1. TypeScript Safety Guard: Check if the form ref actually exists
    if (!form.current) {
      console.error("Form element ref is not attached.");
      return;
    }

    const SERVICE_ID = "your_service_id";
    const TEMPLATE_ID = "your_template_id";
    const PUBLIC_KEY = "your_public_key";

    emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, form.current, PUBLIC_KEY).then(
      (_result) => {
        alert("Email sent successfully!");
      },
      (error) => {
        alert("Failed to send email: " + error.text);
      },
    );

    // 2. This resets your controlled MUI inputs automatically
    setFormData({
      name: "",
      email: "",
      phone: "",
      serviceType: "",
      message: "",
    });
  };

  const serviceTypes = [
    "Climatização e Ar Condicionado",
    "Serviços Elétricos",
    "Construção Civil",
    "Hidráulica e Encanamento",
    "Aplicação de Revestimentos",
    "Instalação de Racks e Estruturas",
    "Consulta Técnica",
    "Orçamento Gratuito",
    "Outro",
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Card
        sx={{
          boxShadow: theme.shadows[4],
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: "white",
                borderRadius: "50%",
                width: 60,
                height: 60,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <FontAwesomeIcon icon={faComments} size="lg" />
            </Box>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
              }}
            >
              Envie sua Mensagem
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: theme.palette.text.secondary }}
            >
              Preencha o formulário abaixo e nossa equipe entrará em contato
            </Typography>
          </Box>

          <form ref={form} onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Seu Nome"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <FontAwesomeIcon
                      icon={faUser}
                      style={{
                        marginRight: 8,
                        color: theme.palette.primary.main,
                      }}
                    />
                  ),
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 3,
                }}
              >
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Telefone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
              </Box>

              <FormControl fullWidth>
                <InputLabel id="service-type-label">Tipo de Serviço</InputLabel>
                <Select
                  labelId="service-type-label"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleSelectChange}
                  label="Tipo de Serviço"
                  required
                >
                  {serviceTypes.map((service, index) => (
                    <MenuItem key={index} value={service}>
                      {service}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Mensagem"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                multiline
                rows={6}
                required
                variant="outlined"
                placeholder="Descreva sua necessidade ou projeto..."
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  py: 1,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                    transform: "translateY(-2px)",
                    boxShadow: theme.shadows[4],
                  },
                  transition: "all 0.3s ease-in-out",
                }}
              >
                Enviar Mensagem
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ContactForm;
